// The one answer to "where is this camera on a plan, and what should we say about it".
//
// 🛑 ONE HELPER, read by five surfaces: the wall tile's Plan button (label, colour and
// tooltip), the detail card's button and sub-line, and the floor-plan modal's opening
// mode and banner. Derived ad hoc in each of them it drifted immediately — the modal
// opened in `View` mode while its own banner asked for a drag, because one place tested
// `placement === 'plan'` and the other tested `floorID != null`.

/**
 * The three states a camera can be in with respect to a floor plan.
 *
 * They come from the server's `placement`, which is the only field that can tell
 * `space` from `plan` — a camera assigned to a space but never dragged has a floorID
 * and no coordinate, and reading floorID alone calls that "placed".
 *
 * - `placed`   — on a plan, with a pin.        (placement: 'plan')
 * - `needsPin` — space known, pin not dropped. (placement: 'space')
 * - `noSpace`  — no space chosen at all.       (placement: 'site' | 'unallocated')
 */
export const PLAN_STATE = Object.freeze({
	PLACED: 'placed',
	NEEDS_PIN: 'needsPin',
	NO_SPACE: 'noSpace'
});

/**
 * Everything any consumer needs to say about a camera's placement.
 *
 * ⚠️ `spaceName` is an EXPLICIT argument and not read off the camera. The wall renders
 * the raw camera objects out of the tree's space buckets, and those carry `floorID` but
 * no name — only the caller holding the floors map can resolve it. Passing it in is what
 * keeps this a pure function testable without a store.
 *
 * @param {Object|null} camera - a camera from the vision bucket
 * @param {string|null} [spaceName] - the resolved name of camera.floorID, if any
 * @returns {Object} state flags plus the copy every surface prints
 */
export const planState = (camera, spaceName = null) => {
	const placement = camera?.placement ?? 'unallocated';
	const placed = placement === 'plan';
	const needsPin = placement === 'space';
	const noSpace = !placed && !needsPin;

	// The space is only nameable in the two states that HAVE one. It no longer reaches the
	// button label at all — only `subLine`, `banner` and `railHeading`, which are sentences
	// and can afford to name a floor.
	const space = placed || needsPin ? spaceName || 'this space' : null;

	const state = placed ? PLAN_STATE.PLACED : needsPin ? PLAN_STATE.NEEDS_PIN : PLAN_STATE.NO_SPACE;

	return {
		state,
		placed,
		needsPin,
		noSpace,
		spaceName: space,

		// 🛑 Amber in BOTH unfinished states, white only when placed. The tile's Plan
		// button, the browser row's chip and the detail button all read this one flag, so
		// a camera that needs work looks the same in all three.
		attention: !placed,

		/**
		 * The name of the journey, wherever it is offered — a tooltip on both surfaces now
		 * that the button itself reads "Floor plan".
		 *
		 * 🛑 TWO states, not three. The unfinished pair used to split into "Drop the pin on
		 * Parking" and "Add to a floor plan", which named the SPACE in one of them: a
		 * per-camera string in what is supposed to be the stable name of one control, and it
		 * described the mechanic ("drop the pin") rather than the outcome. Both unfinished
		 * states are the same errand — this camera is not on a plan yet — so both say so.
		 *
		 * `attention` still separates them from `placed`; the wording no longer has to.
		 */
		label: placed ? 'Show on the floor plan' : 'Add to floor plan',

		/** The detail card's line under the button — the same fact, as a sentence. */
		subLine: placed
			? `Pin dropped on ${space}.`
			: needsPin
				? `In ${space}, but its pin hasn't been dropped yet.`
				: 'Not on a floor plan yet — pick its space and drop the pin.',

		/**
		 * How the modal opens: the finished state LOOKS, both unfinished states WORK.
		 *
		 * 🛑 Every state that is not `placed` opens in EDIT, because View mode cannot accept
		 * a drag and a drag is the entire errand — the button says "Add to floor plan" for
		 * both of them. `needsPin` was fixed first (it opened in View under a banner asking
		 * for a drag); `noSpace` had the same bug one step later, since picking the space in
		 * the modal's own picker left the operator on a plan they still could not drop onto,
		 * with nothing on screen saying that a mode switch was the missing step.
		 *
		 * `placed` stays in View: it was opened to answer "where is this camera", and Edit
		 * over that question is an invitation to move a pin nobody asked to move.
		 */
		mode: placed ? 'View' : 'Edit',

		/** The modal's banner: `type` is an antd Alert type. */
		banner: placed
			? {
					type: 'info',
					message: `View mode — ${camera?.name ?? 'this camera'} is on this plan. Click its pin to highlight it, or switch to Edit to move it.`
				}
			: needsPin
				? {
						type: 'warning',
						message: `Drop the pin — ${camera?.name ?? 'this camera'} is in ${space}. Drag it from the Cameras list onto the plan to say where it sits.`
					}
				: {
						type: 'warning',
						message: `Two steps — choose the space ${camera?.name ?? 'this camera'} watches, then drag it from the Cameras list onto that floor plan.`
					},

		/** The modal rail's heading: a placed or pinless camera has a floor; a spaceless one is still choosing. */
		railHeading: noSpace ? 'WHICH SPACE DOES IT WATCH?' : `CAMERAS ON ${String(space).toUpperCase()}`
	};
};

export default planState;
